/**
 * Adapter — maps the strict GitHub-JSON content models into the legacy
 * `Movie` shape used by existing UI components. This is the bridge that
 * lets the new content layer flow into the unchanged listing components.
 */
import type { Movie } from "../movies-data";
import {
  getAllMovies,
  getAllSeries,
  getSeasonsForSeries,
  getRawMovieBySlug,
  getRawSeriesInfoBySlug,
} from "./loader";
import type { ContentMovie, ContentSeriesInfo } from "./schema";

function movieFromContent(m: ContentMovie): Movie {
  return {
    id: m.id,
    slug: m.slug,
    title: m.title,
    titleEn: m.original_title,
    titleFr: m.title_fr,
    type: "movie",
    year: m.year,
    releaseDate: m.release_date,
    rating: m.rating,
    duration: m.duration,
    genres: m.genres_ar ?? m.genres,
    genresEn: m.genres,
    genresFr: m.genres_fr,
    poster: m.poster,
    backdrop: m.backdrop,
    overview: m.description,
    overviewEn: m.description_en ?? m.description,
    overviewFr: m.description_fr,
    cast: m.cast,
    director: m.director,
    trailer: m.trailer_url ?? m.watch_url ?? "",
    watchToken: m.watch_token,
    downloads: (() => {
      const dls = (m.downloads ?? []).slice();
      // Surface the canonical watch_url as an editable "watch" row so the
      // dashboard always shows the currently-published link for editing.
      if (m.watch_url && !dls.some((d) => d.kind === "watch" && d.url === m.watch_url)) {
        dls.unshift({ kind: "watch", quality: "1080p", size: "", server: "Main", url: m.watch_url });
      }
      return dls;
    })(),
    country: m.country,
    isNew: m.is_new,
    isTrending: m.is_trending,
    tags: m.tags,
    videoSource: m.video_source,
    ...(m.paragraphs ? { paragraphs: m.paragraphs } : {}),
    ...(m.article ? { article: m.article } : {}),
    // Capture the raw on-disk JSON so the dashboard can round-trip all
    // fields (including ones the UI doesn't expose) without data loss.
    __raw: getRawMovieBySlug(m.slug),
  } as Movie;
}


function seriesFromContent(s: ContentSeriesInfo): Movie {
  return {
    id: s.id,
    slug: s.slug,
    title: s.title,
    titleEn: s.original_title,
    titleFr: s.title_fr,
    type: "series",
    year: s.year ?? new Date().getFullYear(),
    firstAirDate: s.first_air_date,
    lastAirDate: s.last_air_date,
    status: s.status,
    statusEn: s.status_en,
    statusFr: s.status_fr,
    rating: s.rating,
    duration: `${s.seasons_count} موسم`,
    genres: s.genres_ar ?? s.genres,
    genresEn: s.genres,
    genresFr: s.genres_fr,
    poster: s.poster,
    backdrop: s.backdrop,
    overview: s.description,
    overviewEn: s.description_en ?? s.description,
    overviewFr: s.description_fr,
    cast: s.cast,
    director: s.director ?? "",
    trailer: s.trailer_url ?? s.watch_url ?? "",
    watchToken: s.watch_token,
    downloads: (() => {
      const dls = (s.downloads ?? []).slice();
      if (s.watch_url && !dls.some((d) => d.kind === "watch" && d.url === s.watch_url)) {
        dls.unshift({ kind: "watch", quality: "1080p", size: "", server: "Main", url: s.watch_url });
      }
      return dls;
    })(),

    country: s.country,
    isNew: s.is_new,
    isTrending: s.is_trending,
    tags: s.tags,
    seasonsCount: s.seasons_count,
    ...(s.paragraphs ? { paragraphs: s.paragraphs } : {}),
    ...(s.article ? { article: s.article } : {}),
    __raw: getRawSeriesInfoBySlug(s.slug),
  } as Movie;
}

export function getContentCatalog(): Movie[] {
  return [...getAllMovies().map(movieFromContent), ...getAllSeries().map(seriesFromContent)];
}

export function getContentEpisodes(seriesSlug: string) {
  return getSeasonsForSeries(seriesSlug).flatMap((season) =>
    season.episodes.map((ep) => ({
      id: `${seriesSlug}-s${season.season}e${ep.episode_number}`,
      seriesSlug,
      season: season.season,
      number: ep.episode_number,
      title: ep.title,
      thumbnail: ep.thumbnail,
      duration: ep.duration,
      sourceId: ep.video_source.source_id,
      addedAt: 0,
    })),
  );
}
